import React, { useContext } from "react";
import { Thumbnail, ResourceItem, InlineStack, Text, Link, Badge, Button } from "@shopify/polaris";
import PropTypes from "prop-types";

import { PrivateContext } from "lib/context";
import { baseHelper, imageHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { useNavigate } from "react-router-dom";

const ProductListView = (props) => {
  const { keyIndex, item, bulkAction, loading } = props;
  const { currentUser, cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { _id: id, productId, title, vendor, vendorId, createdAt, variants = [], published, status, images } = item;
  const isVendor = baseHelper.isVendor(currentUser);
  const isSeller = baseHelper.isSeller(currentUser);
  const productID = isSeller ? productId : id;
  const thumbnailImage =
    images.length > 0
      ? imageHelper.resize({ url: images[0].imageUrl, type: constant.imageTypes.THUMBNAIL })
      : constant.NOIMAGESNAP;
  const renderThumbnail = <Thumbnail source={thumbnailImage} alt={title} />;

  const getShortcutActionsForSeller = (statusProduct, ids) => {
    const productStatus = statusProduct.toLowerCase();
    if (productStatus === constant.NEW.toLowerCase() || productStatus === constant.REVIEWED.toLowerCase()) {
      return (
        <Button
          size="slim"
          loading={loading}
          onClick={() => {
            bulkAction(constant.APPROVE, false, ids, "", null, productStatus);
          }}
        >
          {cms("button.productList.pushToStore.primaryAction")}
        </Button>
      );
    }
    return null;
  };

  let badgeType = constant.DEFAULT;
  const statusColor = status.toLowerCase();
  switch (statusColor) {
    case constant.APPROVED.toLowerCase():
      badgeType = constant.SUCCESS;
      break;
    case constant.REVIEWED.toLowerCase():
    case constant.NEEDS_REVIEW.toLowerCase():
      badgeType = constant.INFO;
      break;
    case constant.REJECTED.toLowerCase():
      badgeType = constant.WARNING;
      break;
    case constant.DELETED.toLowerCase():
      badgeType = constant.DEFAULT;
      break;
    default:
      badgeType = constant.ATTENTION;
      break;
  }

  return (
    <ResourceItem
      media={renderThumbnail}
      id={productID}
      key={keyIndex}
      accessibilityLabel={`${cms("label.productList.viewDetailsFor")} ${title}`}
      persistActions
      recordID={id}
    >
      <InlineStack align="space-between">
        <InlineStack>
          <div className="text-capitalize">
            <Text variant="headingMd" as="h2">
              <Link onClick={() => navigate(`/products/edit/${productID}`)}>
                <div className="ellipsis">{title}</div>
              </Link>
            </Text>
            <Text variant="bodySm" as="p">
              {`${cms("label.productList.addedBy")} `}
              {(isVendor && (
                <Text as="span" fontWeight="semibold">
                  {vendor}
                </Text>
              )) || (
                <Link onClick={() => navigate(`/providers/view/${vendorId}`)}>
                  <Text as="span" fontWeight="semibold">
                    {vendor}
                  </Text>
                </Link>
              )}
              {` ${cms("label.productList.on")} ${baseHelper.formatDate(createdAt)}`}
            </Text>
            <Text variant="bodySm" as="p">
              <Text as="span" tone="subdued">
                {`${cms("label.productList.numberOfVariants")}${variants.length}`}
              </Text>
            </Text>
            {published && (
              <Text variant="bodySm" as="p">
                <Text as="span" tone="subdued">
                  {cms("label.productList.availabilityStatus")}
                  {published ? constant.AVAILABLE : constant.UN_AVAILABLE}
                </Text>
              </Text>
            )}
          </div>
        </InlineStack>
        <InlineStack gap={"400"}>
          <Badge tone={badgeType}>
            <span className="text-capitalize">{status}</span>
          </Badge>
          {getShortcutActionsForSeller(status, productID)}{" "}
        </InlineStack>
      </InlineStack>
    </ResourceItem>
  );
};
ProductListView.defaultProps = {
  keyIndex: "",
  item: {
    _id: "",
    title: "",
    vendor: "",
    createdAt: "",
    variantCount: "",
    published: "",
    productId: "",
    status: "",
    images: { imageUrl: null },
  },
  loading: false,
  bulkAction: () => {},
};

ProductListView.propTypes = {
  keyIndex: PropTypes.number,
  item: PropTypes.shape(PropTypes.object),
  bulkAction: PropTypes.func,
  loading: PropTypes.bool,
};

export default ProductListView;
